import React, { useState, useEffect } from 'react';

const SportScoreInput = ({ sport, onScoreChange, scores = {} }) => {
  const [error, setError] = useState('');
  const [currentSets, setCurrentSets] = useState(3);

  const sportConfigs = {
    Cricket: {
      type: 'team',
      components: ['runs', 'wickets', 'overs'],
      maxWickets: 10,
      maxOvers: 50,
    },
    Football: { type: 'simple', maxScore: 99 },
    Basketball: { type: 'simple', maxScore: 999 },
    Tennis: { type: 'sets', maxSets: 5, minSetsToWin: 3},
    Hockey: { type: 'simple', maxScore: 99 },
    Baseball: { type: 'innings', maxInnings: 9, maxRunsPerInning: 99 },
    Rugby: { type: 'simple', maxScore: 999 },
    Golf: { type: 'holes', holes: 18, maxScorePerHole: 99 },
    Volleyball: { type: 'sets', maxSets: 5, minSetsToWin: 3, maxPointsPerSet: 25 },
    Badminton: { type: 'sets', maxSets: 3, minSetsToWin: 2, maxPointsPerSet: 21 },
    'Table Tennis': { type: 'sets', maxSets: 5, minSetsToWin: 3, maxPointsPerSet: 11 },
  };

  const handleInputChange = (key, value) => {
    const newValue = value === '' ? '' : parseInt(value, 10);
    const newScores = { ...scores, [key]: newValue };
    onScoreChange(newScores);
  };

  const renderCricketScoring = () => (
    <div className="space-y-4">
      {['Team 1', 'Team 2'].map((team, index) => (
        <div key={team} className="grid grid-cols-3 gap-4">
          <h4 className="col-span-3 text-sm font-semibold">{team}</h4>
          {sportConfigs.Cricket.components.map((component) => (
            <div key={component}>
              <label className="block text-sm font-medium mb-1">
                {component.charAt(0).toUpperCase() + component.slice(1)}
              </label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                min="0"
                max={component === 'wickets' ? sportConfigs.Cricket.maxWickets : component === 'overs' ? sportConfigs.Cricket.maxOvers : 999}
                value={scores[`${component}${index + 1}`] ?? ''}
                onChange={(e) => handleInputChange(`${component}${index + 1}`, e.target.value)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderSimpleScoring = () => (
    <div className="grid grid-cols-2 gap-4">
      {['Team 1', 'Team 2'].map((team, index) => (
        <div key={team}>
          <label className="block text-sm font-medium mb-1">{team}</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            min="0"
            max={sportConfigs[sport]?.maxScore || 99}
            value={scores[`team${index + 1}`] ?? ''}
            onChange={(e) => handleInputChange(`team${index + 1}`, e.target.value)}
          />
        </div>
      ))}
    </div>
  );

  const renderSetScoring = () => {
    const addSet = () => {
      if (currentSets < 5) {
        setCurrentSets((prev) => prev + 1);
      }
    };
  
    const removeSet = () => {
      if (currentSets > 3) {
        setCurrentSets((prev) => prev - 1);
      }
    };
  
    return (
      <div className="space-y-4">
        {Array.from({ length: currentSets }, (_, setNum) => (
          <div key={setNum + 1} className="flex items-center space-x-4">
            <span className="w-16 text-gray-600">Set {setNum + 1}</span>
            <input
              type="number"
              className="p-2 border border-gray-300 rounded w-20 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              min="0"
              max={sportConfigs[sport]?.maxPointsPerSet || 99}
              placeholder="Team 1"
              value={scores[`set${setNum + 1}Team1`] ?? ''}
              onChange={(e) => handleInputChange(`set${setNum + 1}Team1`, e.target.value)}
            />
            <input
              type="number"
              className="p-2 border border-gray-300 rounded w-20 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              min="0"
              max={sportConfigs[sport]?.maxPointsPerSet || 99}
              placeholder="Team 2"
              value={scores[`set${setNum + 1}Team2`] ?? ''}
              onChange={(e) => handleInputChange(`set${setNum + 1}Team2`, e.target.value)}
            />
            {setNum + 1 === currentSets && (
              <div className="flex space-x-2">
                {currentSets < 5 && (
                  <button
                    className="w-6 h-6 flex items-center justify-center border border-gray-300 text-gray-600 bg-white hover:bg-gray-50"
                    onClick={addSet}
                  >
                    +
                  </button>
                )}
                {currentSets > 3 && (
                  <button
                    className="w-6 h-6 flex items-center justify-center border border-gray-300 text-gray-600 bg-white hover:bg-gray-50"
                    onClick={removeSet}
                  >
                    -
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (sport) {
      case 'Cricket':
        return renderCricketScoring();
      case 'Football':
      case 'Basketball':
      case 'Hockey':
      case 'Rugby':
        return renderSimpleScoring();
      case 'Tennis':
      case 'Volleyball':
      case 'Badminton':
      case 'Table Tennis':
        return renderSetScoring();
      default:
        return null;
    }
  };

  useEffect(() => {
    onScoreChange({});
    setError('');
  }, [sport]);

  return (
    <div className="w-full max-w-2xl p-6 border rounded shadow">
      <h3 className="text-lg font-semibold mb-4">{sport} Score</h3>
      {renderContent()}
      {error && (
        <div className="mt-4 p-4 bg-red-100 text-red-700 border border-red-300 rounded">
          {error}
        </div>
      )}
    </div>
  );
};

export default SportScoreInput;